import { errorMessageFrom } from "../../../../../../../utils/formErrors";
import { useOrgEmployees } from "../../../../../../../services/useOrgData";
import React, {useState, useEffect} from 'react';
import TopBar from '../../../../../../../component/TopBar';
import {useLocation, useNavigate} from "react-router-dom";
import {Box, Stack, CircularProgress} from '@mui/material';
import ActiveBar from '../../../../../../../component/PolicyNavbarUpdated';
import BtnPrimary from '../../../../../../../component/PrimaryButton';
import BtnSecondary from '../../../../../../../component/SecondaryButton';
import FormField from '../../../../../../../component/Formfield';
import TypeBtn from '../../../../../../../component/AddRemoveButton';
import SuccessDialog from '../../../../../../../component/SuccessDialog';
import SectionLoader from '../../../../../../../component/SectionLoader';
import ErrorMsg from '../../../../../../../component/ErrorMsg';
import { WizardCard, FormSection, FieldLabel } from '../../../../../../../component/wizard';
import { useNotify } from '../../../../../../../component/NotificationProvider';
import { getAttendancePolicyById, updateAttendancePolicy } from '../../../../../../../services/attendancePolicy';
import { tokens } from '../../../../../../../theme';

const AttendancePolicyRules = () => {
  const notify = useNotify();
  const {state} = useLocation();
  const navigate = useNavigate();
  const { data: employees } = useOrgEmployees();
  const [loading,setLoading] = useState(false);
  const [apiCall,setApiCall] = useState(false);
  const [policyId,setPolicyId] = useState("");
  const [approver, setApprover] = useState([""]);
  /**
   * Field-level, and only field-level. "Choose an approver" is a fact about the
   * control and belongs under it; a failed write is not the reader's mistake and
   * goes to a toast instead. They shared one slot before, which made a server
   * failure look like a field somebody had filled in wrongly.
   */
  const [approverError, setApproverError] = useState("");
  const checkApprovers = (list) =>
    (list || []).filter((a) => a).length === 0 ? "Choose at least one approver." : "";
  const [isSuccessDialogOpen,setIsSuccessDialogOpen] = useState(false);

  const approverOptions = (employees || []).map((e) => ({
    name: `${e.fname || ""} ${e.lname || ""}`.trim(),
    value: e._id,
  }));

  useEffect(() => {
    if (state?.policyId) {
      setPolicyId(state.policyId);
      fetchPolicyData(state.policyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const fetchPolicyData = async (id) => {
    setLoading(true);
    try {
      const res = await getAttendancePolicyById(id);
      const ids = res?.data?.approverIds;
      setApprover(Array.isArray(ids) && ids.length ? ids : [""]);
    } catch (error) {
      notify.error(error?.message || "Failed to load policy data");
    } finally {
      setLoading(false);
    }
  };

  const handleAddApproval = () => setApprover([...approver, ""]);
  const handleRemoveApprover = (index) => setApprover(approver.filter((_, i) => i !== index));
  const handleApproverChange = (index, value) => {
    const next = [...approver];
    next[index] = value;
    setApprover(next);
  };

  const handleSave = async () => {
    const cleaned = approver.filter(Boolean);
    if (cleaned.length === 0) {
      setApproverError("Choose at least one approver.");
      return;
    }
    setApiCall(true);
    try {
      const res = await updateAttendancePolicy(policyId, { approverIds: cleaned });
      if (res.success) {
        navigate("/app/attendance-policy/new/review", { state: { policyId } });
      } else {
        notify.error(errorMessageFrom(res, "Could not save approvers"));
      }
    } catch (error) {
      notify.error(error?.message || "Something went wrong saving approvers");
    } finally {
      setApiCall(false);
    }
  };

  return (
    <Box sx={{width:"100%"}}>
      <TopBar
        MainHeading={"Attendance Policy"}
        breadcrumbs={[{label:"Attendance", href:""},{label:"Policy Details", href:""},{label:"Add Category", href:""},{label:"Assign Policy", href:""},{label:"Approval chain", href:""}]}
      />
      <WizardCard>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.5rem" }}>
          <ActiveBar active={["Policy Details","Add Category","Assign Policy","Approval chain"]} />
          <BtnSecondary to="/app/attendance-policy/listing" sx={{ borderRadius: "8px" }}>
            Back
          </BtnSecondary>
        </Stack>

        {loading ? (
          <SectionLoader />
        ) : (
          <FormSection title="Approval chain" description="Who can review and approve attendance requests under this policy.">
            <Stack spacing={1.5}>
              {approver?.map((item, index) => (
                <Stack key={index} direction="row" alignItems="flex-end" spacing={1.5}
                  sx={{ p: "1rem 1.25rem", borderRadius: "12px", border: `1px solid ${tokens.border}`, background: tokens.surface }}>
                  <Box sx={{ flex: 1 }}>
                    <FieldLabel>Approver {index + 1}</FieldLabel>
                    <FormField type="select" options={approverOptions} value={item}
                      onChange={(e) => { handleApproverChange(index, e.target.value); setApproverError(""); }}
                      onBlur={() => setApproverError(checkApprovers(approver))} />
                  </Box>
                  {approver.length > 1 && (
                    <TypeBtn type="remove" onClick={() => handleRemoveApprover(index)}>Remove</TypeBtn>
                  )}
                </Stack>
              ))}
            </Stack>
            <Box sx={{ mt: "1rem" }}>
              <TypeBtn onClick={handleAddApproval} type="add">Add Approver</TypeBtn>
            </Box>
            <ErrorMsg>{approverError}</ErrorMsg>
          </FormSection>
        )}

        <Stack direction="row" justifyContent="flex-end" sx={{ mt: "2rem" }}>
          <BtnPrimary onClick={handleSave} disabled={apiCall || loading}
            sx={{ height: "3rem", px: "2.5rem", borderRadius: "10px" }}>
            {apiCall ? <CircularProgress size={"1.25rem"} /> : "Review"}
          </BtnPrimary>
        </Stack>

        <SuccessDialog
          open={isSuccessDialogOpen}
          onClose={() => setIsSuccessDialogOpen(false)}
          message={`Policy created successfully!`}
          addNewLink={"/app/attendance-policy/new/setting"}
          addNewText="Create New Policy"
          viewAllLink={"/app/attendance-policy/listing"}
          viewAllText="View All Policies"
        />
      </WizardCard>
    </Box>
  )
}

export default AttendancePolicyRules;
